'use client';

import { useEffect, useMemo, useState } from 'react';
import { Library, Loader2, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { IconSvgPreview } from '@/components/painel/icon-library/IconSvgPreview';
import { PainelPermissionsDialogShell } from '@/components/painel/permissions/painel-role-visuals';
import { painelInputClass } from '@/components/painel/cms/painel-form';
import {
  painelButtonPrimaryClass,
  painelModalCancelButtonClass,
} from '@/components/painel/painel-styles';
import { categoryLabel, type IconLibraryItem } from '@/lib/icon-library';
import { cn } from '@/lib/utils';
import { fetchAdminIcons, fetchPublicIcons } from '@/services/icon-library.service';

type IconLibraryPickerProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelect: (item: IconLibraryItem) => void;
  /** Usa API admin (requer icons.manage) ou pública. */
  auth?: boolean;
  title?: string;
};

export function IconLibraryPicker({
  open,
  onOpenChange,
  onSelect,
  auth = true,
  title = 'Biblioteca de ícones',
}: IconLibraryPickerProps) {
  const [q, setQ] = useState('');
  const [items, setItems] = useState<IconLibraryItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [selected, setSelected] = useState<IconLibraryItem | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setLoading(true);
    setSelected(null);
    const load = auth ? fetchAdminIcons({ q }) : fetchPublicIcons({ q });
    load
      .then((list) => {
        if (!cancelled) setItems(list);
      })
      .catch(() => {
        if (!cancelled) setItems([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, q, auth]);

  const filtered = useMemo(() => items, [items]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        showCloseButton={false}
        className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden border-0 bg-[#030023] p-0 shadow-none"
      >
        <DialogTitle className="sr-only">{title}</DialogTitle>
        <PainelPermissionsDialogShell className="min-h-0 flex-1 overflow-hidden">
          <div className="shrink-0 border-b border-white/10 bg-blue-950/30 px-6 py-5 sm:px-8">
            <div className="flex items-start gap-3">
              <span className="inline-flex h-11 w-11 items-center justify-center rounded-xl bg-sky-500/15 ring-1 ring-sky-400/30">
                <Library className="h-5 w-5 text-sky-300" />
              </span>
              <div>
                <h2 className="font-poppins text-lg font-bold text-white">{title}</h2>
                <p className="mt-0.5 font-inter text-sm text-slate-400">
                  Pesquise e selecione um ícone da Icon Library.
                </p>
              </div>
            </div>
            <div className="relative mt-4">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-500" />
              <Input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Buscar…"
                className={cn(painelInputClass, 'pl-9')}
              />
            </div>
          </div>

          <div className="min-h-0 flex-1 overflow-y-auto px-6 py-4 sm:px-8">
            {loading ? (
              <div className="flex items-center justify-center gap-2 py-12 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
                <span className="text-sm">A carregar…</span>
              </div>
            ) : filtered.length === 0 ? (
              <p className="py-10 text-center font-inter text-sm text-slate-500">
                Nenhum ícone encontrado.
              </p>
            ) : (
              <div className="grid grid-cols-3 gap-2 sm:grid-cols-4 md:grid-cols-5">
                {filtered.map((item) => {
                  const active = selected?.id === item.id;
                  return (
                    <button
                      key={item.id}
                      type="button"
                      onClick={() => setSelected(item)}
                      className={cn(
                        'flex flex-col items-center gap-2 rounded-xl border p-3 text-center transition',
                        active
                          ? 'border-sky-400/50 bg-sky-500/15 ring-1 ring-sky-400/40'
                          : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.06]'
                      )}
                    >
                      <IconSvgPreview
                        url={item.url}
                        renderMode={item.renderMode}
                        size={40}
                        background="dark"
                      />
                      <span className="line-clamp-1 w-full font-inter text-[10px] text-slate-300">
                        {item.name}
                      </span>
                      <span className="line-clamp-1 w-full font-mono text-[9px] text-slate-500">
                        {categoryLabel(item.category)}
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="flex shrink-0 flex-col-reverse gap-2 border-t border-white/10 bg-blue-950/40 px-6 py-4 sm:flex-row sm:justify-end sm:gap-3 sm:px-8">
            <Button
              type="button"
              variant="ghost"
              className={painelModalCancelButtonClass}
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="button"
              className={cn(painelButtonPrimaryClass, 'h-10 px-5')}
              disabled={!selected}
              onClick={() => {
                if (!selected) return;
                onSelect(selected);
                onOpenChange(false);
              }}
            >
              Usar ícone
            </Button>
          </div>
        </PainelPermissionsDialogShell>
      </DialogContent>
    </Dialog>
  );
}
